'use client';

import React from 'react';
import { useFormHelper } from '@plura/client/utils';
import cn from "classnames";
import type { PlacesType } from 'react-tooltip';
import Icon, { type IconName } from '../../atoms/Icon';
import Tooltip from '../../atoms/Tooltip';
import '../_shared/conversation-form-molecules.css';


type InputProps = React.InputHTMLAttributes<HTMLInputElement> &
{
  label?: string;
  error?: string;
  className?: string;
  classLabel?: string;
  classInput?: string;
  icon?: IconName;
  copy?: boolean;
  tooltip?: string;
  place?: PlacesType;
  trailingComponent?: React.ReactNode;
};

const inputClass = cn(
  'w-full h-12 rounded-xl border-2 border-transparent bg-[#f4f4f4] px-2.5 font-[Inter,sans-serif] text-[15px] font-semibold leading-[24px] text-[#1a1d1f] transition-all duration-200',
  '[.dark-mode_&]:border-[#272b30] [.dark-mode_&]:bg-[#272b30] [.dark-mode_&]:text-[#fcfcfc]',
  'placeholder:text-[#6f767e] [.dark-mode_&]:placeholder:text-[#9a9fa5]',
  'focus:border-[#9a9fa5] focus:bg-white focus:outline-none [.dark-mode_&]:focus:border-[#33383f] [.dark-mode_&]:focus:bg-[#111315]',
  'disabled:text-[#6f767e] [.dark-mode_&]:disabled:text-[#9a9fa5]',
  '[&:-webkit-autofill]:[transition:background-color_100000s_ease-in-out_0s]',
);

const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    label,
    className,
    classLabel,
    classInput,
    icon,
    copy,
    tooltip,
    place,
    trailingComponent,
    ...rest
  } = props;

  const { onValueChange, errorMessage, ...formHelperProps } = useFormHelper(rest);

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className={cn(classLabel, 'mb-3.5 block text-sm font-semibold leading-[24px] text-[#33383f] [.dark-mode_&]:text-[#efefef]')}>
          {label}{" "}
          {tooltip && (
            <Tooltip
              className="relative -top-px [&_svg]:fill-[#9a9fa5]"
              content={tooltip}
              place={place ? place : "right"}
            />
          )}
        </label>
      )}
      <div className={cn('plura-field-wrap', !!errorMessage && 'plura-field-wrap--error')}>
        <input
          ref={ref}
          {...rest}
          {...formHelperProps}
          className={cn(
            inputClass,
            icon && 'pl-12',
            (copy || errorMessage) && 'pr-12',
            !!errorMessage && 'border-transparent bg-[rgba(255,188,153,0.25)] text-[#ff6a55] placeholder:text-[#ff6a55] [.dark-mode_&]:focus:border-[#ff6a55]',
            classInput,
          )}
          data-gramm="false"
          data-gramm_editor="false"
          data-enable-grammarly="false"
        />
        {icon !== undefined && (
          <div className="pointer-events-none absolute bottom-0 left-0 top-0 flex h-12 w-12 items-center justify-center [&_svg]:fill-[#6f767e]">
            <Icon name={icon} className="!size-6" />
          </div>
        )}
        {copy && (
          <button type="button" className="absolute bottom-0 right-0 top-0 flex h-12 w-12 items-center justify-center [&_svg]:fill-[#6f767e] transition-[fill] duration-200 hover:[&_svg]:fill-[#2a85ff]">
            <Icon name="copy" className="!size-6" />{" "}
          </button>
        )}

        {
          trailingComponent && (
            <div className="absolute right-1 top-1 z-[2]">
              {trailingComponent}
            </div>
          )
        }

        {errorMessage && (
          <p className="mt-1 text-right text-sm text-[#ff6a55]">{errorMessage}</p>
        )}
      </div>
    </div>
  );
});

Input.displayName = 'Input';

export default Input
